// Audit log — every support action and impersonation, newest first. Read-
// only: entries can't be edited or deleted from the console.

import clsx from "clsx";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { AdminAuditPage } from "@paceroo/domain/views/admin";
import { AuditDiff } from "../components/AuditDiff";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { fDT, n0, parseWhen } from "../lib/format";
import { useShell } from "../shell/context";
import s from "./Audit.module.css";

/** Bans and deletions in alert, impersonation in caution. */
export function actionColor(action: string): string {
  if (/ban|delete/i.test(action)) return "var(--alert)";
  if (/impersonat/i.test(action)) return "var(--caution)";
  return "var(--ink)";
}

export function Audit() {
  const { bp, focusSearch } = useShell();
  const q = useAdminQuery<AdminAuditPage>("audit?limit=500");
  const [open, setOpen] = useState<string | null>(null);
  if (q.error && !q.data) return <ErrorState page="Audit log" error={q.error} onRetry={q.reload} onFind={focusSearch} />;
  if (!q.data) return <LoadingState page="audit log" />;

  const entries = q.data.entries;
  const mobile = bp === "mobile";
  const wide = bp === "desktop";
  const cols = mobile ? "1fr" : wide ? "108px 190px 140px minmax(0,1fr) 160px" : "96px 170px 120px minmax(0,1fr)";

  return (
    <div className={s.page}>
      <PageHeader kicker={`Audit log · ${n0(entries.length)} entries`} before="Every change, " accent="on record." />
      <p className={clsx("t-body-sm ink-mute", s.note)}>Written by the support actions and impersonation sessions. Entries can’t be edited or deleted from the console.</p>

      <div className={s.table}>
        {!mobile && entries.length > 0 && (
          <div className={s.head} style={{ gridTemplateColumns: cols }}>
            <span className="kicker ink-mute">When</span>
            <span className="kicker ink-mute">Action</span>
            <span className="kicker ink-mute">Runner</span>
            <span className="kicker ink-mute">Reason</span>
            {wide && <span className="kicker ink-mute">Admin</span>}
          </div>
        )}
        {entries.map((a) => {
          const isOpen = open === a.id;
          return (
            <div key={a.id} className={s.item}>
              <button className={s.row} style={{ gridTemplateColumns: cols }} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : a.id)}>
                <span className="t-mono-xs ink-mute">{fDT(parseWhen(a.at))}</span>
                <span className="t-label" style={{ color: actionColor(a.action) }}>
                  {a.action}
                </span>
                <span className={clsx("t-body-sm", s.ellipsis)}>{a.runner?.name ?? (a.targetUserId ? "Deleted account" : "—")}</span>
                <span className="t-body-sm ink-soft">{a.reason}</span>
                {wide && <span className={clsx("t-mono-xs ink-mute", s.ellipsis)}>{a.adminEmail}</span>}
              </button>
              {isOpen && (
                <div className={s.detail}>
                  <AuditDiff entry={a} />
                  {a.targetUserId && a.runner && (
                    <Link className={clsx("btn-tertiary", s.open)} to={`/runners/${encodeURIComponent(a.targetUserId)}`}>
                      Open {a.runner.name} →
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {entries.length === 0 && (
          <div className={s.empty}>
            <span className="t-serif-md">Nothing on record yet.</span>
            <span className="t-body-sm ink-mute">The first support action you take will appear here with its reason.</span>
          </div>
        )}
      </div>
    </div>
  );
}
